import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { recorrenciasApi, categoriasApi, contasApi } from '../../api/endpoints'
import { Button, IconButton } from '../../components/ui/Button'
import { Input, Select } from '../../components/ui/Input'
import { Card, CardHeader, AvatarIcon } from '../../components/ui/Card'
import { Badge, DonoBadge } from '../../components/ui/Badge'
import { LoadingState, EmptyState, ErrorState } from '../../components/ui/EmptyState'
import { Modal, ConfirmDialog } from '../../components/ui/Modal'
import { Table, THead, Th, Tr, Td } from '../../components/ui/Table'
import { Tabs } from '../../components/ui/Navigation'
import { useToast } from '../../components/ui/Toast'
import { PageHeader } from '../../layout/PageHeader'
import {
  TransactionType, TransactionStatus, TipoRecorrencia,
  TRANSACTION_TYPE_LABEL, TIPO_RECORRENCIA_LABEL,
} from '../../lib/enums'
import { formatMoney, formatDate, cn } from '../../lib/utils'
import type { RecurringTransaction } from '../../types'

interface FormState {
  accountId: string
  description: string
  amount: string
  type: TransactionType
  categoriaId: string
  tipoRecorrencia: TipoRecorrencia
  dataInicio: string
  quantidadeParcelas: string
  dataFim: string
  adicionarNumeroParcela: boolean
  notes: string
  semLancamentoAtePassado: boolean
  descontoDeRecorrenciaId: string
}

function emptyForm(): FormState {
  return {
    accountId: '', description: '', amount: '', type: TransactionType.Expense,
    categoriaId: '', tipoRecorrencia: TipoRecorrencia.Mensal,
    dataInicio: new Date().toISOString().slice(0, 10), quantidadeParcelas: '12',
    dataFim: '', adicionarNumeroParcela: true, notes: '', semLancamentoAtePassado: false,
    descontoDeRecorrenciaId: '',
  }
}

function adicionarMeses(data: Date, meses: number) {
  const dia = data.getUTCDate()
  const resultado = new Date(data)
  resultado.setUTCDate(1)
  resultado.setUTCMonth(resultado.getUTCMonth() + meses)
  const ultimoDia = new Date(Date.UTC(resultado.getUTCFullYear(), resultado.getUTCMonth() + 1, 0)).getUTCDate()
  resultado.setUTCDate(Math.min(dia, ultimoDia))
  return resultado
}

function proximaParcela(data: Date, tipo: TipoRecorrencia) {
  const resultado = new Date(data)
  if (tipo === TipoRecorrencia.Diaria || tipo === TipoRecorrencia.Personalizada) resultado.setUTCDate(resultado.getUTCDate() + 1)
  else if (tipo === TipoRecorrencia.Semanal) resultado.setUTCDate(resultado.getUTCDate() + 7)
  else if (tipo === TipoRecorrencia.Quinzenal) resultado.setUTCDate(resultado.getUTCDate() + 14)
  else if (tipo === TipoRecorrencia.Mensal) return adicionarMeses(resultado, 1)
  else if (tipo === TipoRecorrencia.Bimestral) return adicionarMeses(resultado, 2)
  else if (tipo === TipoRecorrencia.Trimestral) return adicionarMeses(resultado, 3)
  else if (tipo === TipoRecorrencia.Anual) return adicionarMeses(resultado, 12)
  return resultado
}

function calcularDataFinal(dataInicio: string, tipo: TipoRecorrencia, quantidade: number) {
  if (!dataInicio || !Number.isInteger(quantidade) || quantidade < 1) return null
  let data = new Date(`${dataInicio}T00:00:00Z`)
  for (let parcela = 1; parcela < quantidade; parcela++) data = proximaParcela(data, tipo)
  return data.toISOString().slice(0, 10)
}

const FORM_TYPES: TransactionType[] = [TransactionType.Income, TransactionType.Expense]

type Filtro = 'todas' | 'ativas' | 'pausadas'

const FILTROS = [
  { key: 'todas' as const, label: 'Todas', icon: 'ri-list-check-2' },
  { key: 'ativas' as const, label: 'Ativas', icon: 'ri-play-circle-line' },
  { key: 'pausadas' as const, label: 'Pausadas', icon: 'ri-pause-circle-line' },
]

export function RecorrenciasPage() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const toast = useToast()
  const [modalOpen, setModalOpen] = useState(false)
  const [editando, setEditando] = useState<RecurringTransaction | null>(null)
  const [form, setForm] = useState<FormState>(emptyForm())
  const [erro, setErro] = useState<string | null>(null)
  const [paraExcluir, setParaExcluir] = useState<RecurringTransaction | null>(null)
  const [paraEncerrar, setParaEncerrar] = useState<RecurringTransaction | null>(null)
  const [adiantar, setAdiantar] = useState<{ recorrencia: RecurringTransaction; meses: string } | null>(null)
  const [filtro, setFiltro] = useState<Filtro>('todas')
  const location = useLocation()

  // "Virar recorrente" da projeção (Próximos meses): abre o formulário já preenchido com a entrada simulada.
  useEffect(() => {
    const nova = (location.state as { novaRecorrencia?: Partial<FormState> } | null)?.novaRecorrencia
    if (!nova) return
    setEditando(null)
    setForm({ ...emptyForm(), quantidadeParcelas: '', ...nova })
    setErro(null)
    setModalOpen(true)
    navigate(location.pathname, { replace: true, state: null })
  }, [location.state, location.pathname, navigate])

  const { data: page, isLoading, isError } = useQuery({
    queryKey: ['recorrencias'],
    queryFn: () => recorrenciasApi.list(),
  })
  const { data: categorias = [] } = useQuery({
    queryKey: ['categorias'],
    queryFn: () => categoriasApi.list({ incluirInativas: false }),
    enabled: modalOpen,
  })
  const { data: contas = [] } = useQuery({ queryKey: ['contas'], queryFn: () => contasApi.list(), enabled: modalOpen })

  const atualizar = useMutation({
    mutationFn: () => recorrenciasApi.atualizar(editando!.id, {
      description: form.description,
      amount: Number(form.amount),
      type: form.type,
      categoriaId: form.categoriaId || undefined,
      tipoRecorrencia: form.tipoRecorrencia,
      quantidadeParcelas: form.quantidadeParcelas ? Number(form.quantidadeParcelas) : null,
      dataFim: form.dataFim || null,
      removerDataFim: !form.quantidadeParcelas && !form.dataFim,
      adicionarNumeroParcela: form.adicionarNumeroParcela,
      notes: form.notes || undefined,
      ...(form.type === TransactionType.Expense
        ? form.descontoDeRecorrenciaId
          ? { descontoDeRecorrenciaId: form.descontoDeRecorrenciaId }
          : editando?.descontoDeRecorrenciaId ? { removerDescontoDeRecorrencia: true } : {}
        : {}),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['recorrencias'] })
      toast.success('Recorrência atualizada.')
      closeModal()
    },
    onError: (e: Error) => setErro(e.message),
  })

  const alternarPausa = useMutation({
    mutationFn: (r: RecurringTransaction) => recorrenciasApi.atualizar(r.id, { ativa: !r.ativa }),
    onSuccess: (_, r) => {
      qc.invalidateQueries({ queryKey: ['recorrencias'] })
      toast.success(r.ativa ? 'Recorrência pausada. Ela para de gerar lançamentos.' : 'Recorrência retomada.')
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const encerrar = useMutation({
    mutationFn: (r: RecurringTransaction) => recorrenciasApi.atualizar(r.id, {
      ativa: false,
      dataFim: new Date().toISOString().slice(0, 10),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['recorrencias'] })
      toast.success('Recorrência encerrada.')
      setParaEncerrar(null)
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const criar = useMutation({
    mutationFn: () => recorrenciasApi.criar({
      accountId: form.accountId,
      description: form.description,
      amount: Number(form.amount),
      type: form.type,
      status: TransactionStatus.Confirmed,
      categoriaId: form.categoriaId || undefined,
      tipoRecorrencia: form.tipoRecorrencia,
      intervalo: 1,
      dataInicio: form.dataInicio,
      quantidadeParcelas: form.quantidadeParcelas ? Number(form.quantidadeParcelas) : null,
      dataFim: form.dataFim || null,
      adicionarNumeroParcela: form.adicionarNumeroParcela,
      notes: form.notes || undefined,
      semLancamentoAtePassado: form.semLancamentoAtePassado,
      descontoDeRecorrenciaId: form.type === TransactionType.Expense && form.descontoDeRecorrenciaId ? form.descontoDeRecorrenciaId : undefined,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['recorrencias'] })
      qc.invalidateQueries({ queryKey: ['transacoes'] })
      toast.success('Recorrência criada e primeiro lançamento gerado.')
      closeModal()
    },
    onError: (e: Error) => setErro(e.message),
  })

  const gerar = useMutation({
    mutationFn: ({ id, meses }: { id: string; meses: number }) => recorrenciasApi.gerar(id, meses),
    onSuccess: (_, { meses }) => {
      qc.invalidateQueries({ queryKey: ['transacoes'] })
      qc.invalidateQueries({ queryKey: ['recorrencias'] })
      qc.invalidateQueries({ queryKey: ['dashboard'] })
      toast.success(`Lançamentos dos próximos ${meses} ${meses === 1 ? 'mês' : 'meses'} gerados.`)
      setAdiantar(null)
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const remover = useMutation({
    mutationFn: (id: string) => recorrenciasApi.remover(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['recorrencias'] })
      toast.success('Recorrência desativada.')
      setParaExcluir(null)
    },
    onError: (e: Error) => toast.error(e.message),
  })

  function openNew() {
    setEditando(null)
    setForm(emptyForm())
    setErro(null)
    setModalOpen(true)
  }

  function openEdit(r: RecurringTransaction) {
    setEditando(r)
    setForm({
      accountId: r.accountId,
      description: r.description,
      amount: String(r.amount),
      type: r.type,
      categoriaId: r.categoriaId ?? '',
      tipoRecorrencia: r.tipoRecorrencia,
      dataInicio: r.dataInicio.slice(0, 10),
      quantidadeParcelas: r.quantidadeParcelas ? String(r.quantidadeParcelas) : '',
      dataFim: r.quantidadeParcelas ? '' : r.dataFim?.slice(0, 10) ?? '',
      adicionarNumeroParcela: r.adicionarNumeroParcela,
      notes: r.notes ?? '',
      semLancamentoAtePassado: false,
      descontoDeRecorrenciaId: r.descontoDeRecorrenciaId ?? '',
    })
    setErro(null)
    setModalOpen(true)
  }

  function closeModal() {
    setModalOpen(false)
    setEditando(null)
    setErro(null)
  }

  function submit() {
    const quantidadeParcelas = Number(form.quantidadeParcelas)
    if (!form.description.trim() || !form.amount || (!editando && !form.accountId)) {
      setErro('Preencha conta, descrição e valor.')
      return
    }
    if (form.quantidadeParcelas && (!Number.isInteger(quantidadeParcelas) || quantidadeParcelas < 1 || quantidadeParcelas > 600)) {
      setErro('Informe uma quantidade de parcelas entre 1 e 600.')
      return
    }
    if (form.dataFim && form.dataFim < form.dataInicio) {
      setErro('A data final não pode ser anterior à data de início.')
      return
    }
    if (editando) atualizar.mutate()
    else criar.mutate()
  }

  const todas = page?.itens ?? []
  const items = todas.filter((r) => filtro === 'todas' || (filtro === 'ativas' ? r.ativa : !r.ativa))
  const dataFinalCalculada = calcularDataFinal(
    form.dataInicio, form.tipoRecorrencia, Number(form.quantidadeParcelas),
  )
  const dataInicioNoPassado = !editando && !!form.dataInicio && form.dataInicio < new Date().toISOString().slice(0, 10)

  return (
    <>
      <PageHeader title="Recorrências" />

      <Card>
        <CardHeader
          title="Lançamentos recorrentes"
          actions={
            <>
              <span className="text-13 text-muted"><i className="ri-information-line" aria-hidden /> O mês corrente é lançado sozinho, como pendente.</span>
              <Button data-tutorial="recorrencia.nova" icon="ri-add-line" onClick={openNew}>Nova recorrência</Button>
            </>
          }
        />
        <Tabs tabs={FILTROS} active={filtro} onChange={setFiltro} className="px-4" />
        {isLoading ? (
          <LoadingState />
        ) : isError ? (
          <ErrorState message="Erro ao carregar recorrências." />
        ) : items.length === 0 ? (
          <EmptyState icon="ri-repeat-line" message="Nenhuma recorrência cadastrada." action={<Button variant="soft-primary" icon="ri-add-line" onClick={openNew}>Nova recorrência</Button>} />
        ) : (
          <Table>
            <THead>
              <tr><Th>Descrição</Th><Th>Frequência</Th><Th>Período</Th><Th>Próxima</Th><Th>Status</Th><Th className="text-right">Valor</Th><Th className="text-right">Ações</Th></tr>
            </THead>
            <tbody>
              {items.map((r) => {
                const receita = r.type === TransactionType.Income
                return (
                  <Tr key={r.id}>
                    <Td>
                      <div className="flex items-center gap-3">
                        <AvatarIcon icon={receita ? 'ri-arrow-left-down-line' : 'ri-arrow-right-up-line'} tone={receita ? 'income' : 'expense'} size="sm" />
                        <div className="min-w-0">
                          <p className="flex items-center gap-2 font-medium text-white">
                            <span className="truncate">{r.description}</span>
                            <DonoBadge nome={r.ownerNome} />
                          </p>
                          <p className="truncate text-2xs text-muted">{r.accountName}{r.categoriaNome ? ` · ${r.categoriaNome}` : ''}</p>
                        </div>
                      </div>
                    </Td>
                    <Td><Badge variant="info">{TIPO_RECORRENCIA_LABEL[r.tipoRecorrencia] ?? r.tipoRecorrencia}</Badge></Td>
                    <Td className="whitespace-nowrap text-muted">
                      {formatDate(r.dataInicio)}
                      {r.dataFim ? ` até ${formatDate(r.dataFim)}` : <span className="ml-1 text-2xs text-muted/70">sem data fim</span>}
                      {r.quantidadeParcelas && <span className="ml-1 text-2xs text-muted/70">· {r.quantidadeParcelas} parcelas</span>}
                    </Td>
                    <Td className="whitespace-nowrap text-white">{r.ativa ? formatDate(r.proximaOcorrencia) : '—'}</Td>
                    <Td><Badge variant={r.ativa ? 'income' : 'muted'}>{r.ativa ? 'Ativa' : 'Inativa'}</Badge></Td>
                    <Td className={cn('whitespace-nowrap text-right font-semibold', receita ? 'text-income' : 'text-expense')}>{receita ? '+' : '-'}{formatMoney(r.amount)}</Td>
                    <Td className="whitespace-nowrap text-right">
                      <IconButton icon="ri-file-list-3-line" label={`Ver ocorrências de ${r.description}`} tone="muted" onClick={() => navigate(`/recorrencias/${r.id}`)} />
                      <IconButton
                        icon={r.ativa ? 'ri-pause-circle-line' : 'ri-play-circle-line'}
                        label={r.ativa ? `Pausar ${r.description}` : `Retomar ${r.description}`}
                        tone={r.ativa ? 'primary' : 'success'}
                        disabled={alternarPausa.isPending}
                        onClick={() => alternarPausa.mutate(r)}
                      />
                      <IconButton icon="ri-pencil-fill" label={`Editar ${r.description}`} tone="info" onClick={() => openEdit(r)} />
                      <IconButton
                        icon="ri-calendar-schedule-line"
                        label={`Adiantar lançamentos de ${r.description}`}
                        tone="muted"
                        disabled={!r.ativa}
                        onClick={() => setAdiantar({ recorrencia: r, meses: '3' })}
                      />
                      <IconButton icon="ri-stop-circle-line" label={`Encerrar ${r.description}`} tone="danger" disabled={!r.ativa} onClick={() => setParaEncerrar(r)} />
                    </Td>
                  </Tr>
                )
              })}
            </tbody>
          </Table>
        )}
      </Card>

      <Modal
        open={modalOpen}
        onClose={closeModal}
        title={editando ? 'Editar recorrência' : 'Nova recorrência'}
        size="lg"
        footer={
          <>
            <Button variant="light" onClick={closeModal}>Cancelar</Button>
            <Button icon={editando ? 'ri-save-line' : 'ri-add-line'} onClick={submit} loading={criar.isPending || atualizar.isPending}>
              {editando ? 'Salvar' : 'Criar recorrência'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <span className="form-label">Tipo</span>
            <div className="grid grid-cols-2 gap-2">
              {FORM_TYPES.map((tp) => (
                <button
                  key={tp}
                  type="button"
                  aria-pressed={form.type === tp}
                  onClick={() => setForm((f) => ({ ...f, type: tp }))}
                  className={cn('h-[38px] rounded border text-13 transition-colors', form.type === tp ? 'border-primary bg-primary/10 text-primary' : 'border-border/70 text-muted hover:text-white')}
                >
                  {TRANSACTION_TYPE_LABEL[tp]}
                </button>
              ))}
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Input label="Descrição" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="Ex: Aluguel" />
            <Select label="Conta" value={form.accountId} disabled={!!editando} onChange={(e) => setForm((f) => ({ ...f, accountId: e.target.value }))}>
              <option value="">Selecione...</option>
              {contas.map((c) => <option key={c.id} value={c.id}>{c.name} · {c.institution}</option>)}
            </Select>
            <Input label="Valor (R$)" type="number" min={0} step="0.01" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} placeholder="0,00" />
            <Select data-tutorial="recorrencia.periodicidade" label="Frequência" value={form.tipoRecorrencia} onChange={(e) => setForm((f) => ({ ...f, tipoRecorrencia: Number(e.target.value) as TipoRecorrencia }))}>
              {Object.entries(TIPO_RECORRENCIA_LABEL).map(([valor, label]) => <option key={valor} value={valor}>{label}</option>)}
            </Select>
            <Input label="Data de início" type="date" value={form.dataInicio} disabled={!!editando} onChange={(e) => setForm((f) => ({ ...f, dataInicio: e.target.value }))} />
            {dataInicioNoPassado && (
              <label className="flex items-start gap-2 rounded border border-border/70 px-3 py-2.5 sm:col-span-2">
                <input
                  type="checkbox"
                  checked={form.semLancamentoAtePassado}
                  onChange={(e) => setForm((f) => ({ ...f, semLancamentoAtePassado: e.target.checked }))}
                  className="mt-0.5 h-4 w-4 accent-primary"
                />
                <span>
                  <span className="block text-13 text-white">Não gerar lançamento para as ocorrências já vencidas</span>
                  <span className="block text-2xs text-muted">
                    Registra as ocorrências entre o início e hoje como já acontecidas (sem afetar saldo/relatórios), mas contando certo na numeração. Se desmarcado, elas entram como lançamentos pendentes normais.
                  </span>
                </span>
              </label>
            )}
            <div>
              <Input
                label="Quantidade de parcelas"
                type="number" min={1} max={600} step={1}
                value={form.quantidadeParcelas}
                onChange={(e) => setForm((f) => ({
                  ...f,
                  quantidadeParcelas: e.target.value,
                  dataFim: e.target.value ? '' : f.dataFim,
                }))}
                placeholder="Ex: 12"
              />
              {dataFinalCalculada && (
                <p className="mt-1 text-2xs text-muted">
                  Última parcela em {formatDate(dataFinalCalculada)}.
                </p>
              )}
            </div>
            <Input
              label="Data final"
              type="date"
              min={form.dataInicio}
              value={form.dataFim}
              onChange={(e) => setForm((f) => ({
                ...f,
                dataFim: e.target.value,
                quantidadeParcelas: e.target.value ? '' : f.quantidadeParcelas,
              }))}
            />
            <p className="self-center text-2xs text-muted">
              Preencha a quantidade ou a data final. Deixe ambos vazios para uma recorrência por prazo indeterminado.
            </p>
            <label className="flex items-start gap-2 rounded border border-border/70 px-3 py-2.5 sm:col-span-2">
              <input
                type="checkbox"
                checked={form.adicionarNumeroParcela}
                disabled={!form.quantidadeParcelas}
                onChange={(e) => setForm((f) => ({ ...f, adicionarNumeroParcela: e.target.checked }))}
                className="mt-0.5 h-4 w-4 accent-primary disabled:opacity-50"
              />
              <span>
                <span className="block text-13 text-white">Adicionar “parcela X de Y” ao nome</span>
                <span className="block text-2xs text-muted">
                  {form.quantidadeParcelas
                    ? `Exemplo: ${form.description || 'Descrição'} - parcela 1 de ${form.quantidadeParcelas}`
                    : 'Disponível quando houver quantidade de parcelas.'}
                </span>
              </span>
            </label>
            <Select label="Categoria" value={form.categoriaId} onChange={(e) => setForm((f) => ({ ...f, categoriaId: e.target.value }))}>
              <option value="">Sem categoria</option>
              {categorias.map((c) => <option key={c.id} value={c.id}>{c.icone} {c.nome}</option>)}
            </Select>
            <Input label="Observação" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} placeholder="Opcional" />
            {form.type === TransactionType.Expense && (
              <div className="sm:col-span-2">
                <Select
                  label="Desconto automático de (opcional)"
                  value={form.descontoDeRecorrenciaId}
                  onChange={(e) => setForm((f) => ({ ...f, descontoDeRecorrenciaId: e.target.value }))}
                >
                  <option value="">Nenhuma — despesa independente</option>
                  {todas.filter((r) => r.type === TransactionType.Income).map((r) => (
                    <option key={r.id} value={r.id}>{r.description} · {formatMoney(r.amount)}</option>
                  ))}
                </Select>
                <p className="mt-1 text-2xs text-muted">
                  Marca essa despesa como um desconto que já sai antes do depósito cair (ex: convênio no salário).
                  Ao sincronizar com a Pluggy, o valor líquido é casado contra receita − descontos vinculados do mês.
                </p>
              </div>
            )}
            {form.type === TransactionType.Income && !!editando?.descontosVinculados?.length && (
              <div className="sm:col-span-2">
                <span className="form-label">Descontos automáticos vinculados</span>
                <div className="flex flex-wrap gap-1.5">
                  {editando.descontosVinculados.map((d) => (
                    <Badge key={d.id} variant="muted">{d.description} · {formatMoney(d.amount)}</Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
          {erro && <p className="flex items-center gap-1.5 rounded bg-expense/10 px-3 py-2 text-13 text-expense" role="alert"><i className="ri-error-warning-line" aria-hidden /> {erro}</p>}
        </div>
      </Modal>

      <Modal
        open={!!adiantar}
        onClose={() => setAdiantar(null)}
        title={`Adiantar lançamentos · ${adiantar?.recorrencia.description ?? ''}`}
        size="sm"
        footer={
          <>
            <Button variant="light" onClick={() => setAdiantar(null)}>Cancelar</Button>
            <Button
              icon="ri-calendar-schedule-line"
              loading={gerar.isPending}
              onClick={() => {
                const meses = Number(adiantar?.meses)
                if (!Number.isInteger(meses) || meses < 1 || meses > 60) return
                gerar.mutate({ id: adiantar!.recorrencia.id, meses })
              }}
            >
              Gerar
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Input
            label="Quantos meses adiantar"
            type="number" min={1} max={60} step={1}
            value={adiantar?.meses ?? ''}
            onChange={(e) => setAdiantar((a) => a && { ...a, meses: e.target.value })}
            autoFocus
          />
          <p className="text-2xs text-muted">
            Gera as ocorrências a partir de {adiantar ? formatDate(adiantar.recorrencia.proximaOcorrencia) : '—'}, todas como pendentes.
            Datas já lançadas são ignoradas, então repetir não duplica.
          </p>
        </div>
      </Modal>

      <ConfirmDialog
        open={!!paraEncerrar}
        onClose={() => setParaEncerrar(null)}
        onConfirm={() => paraEncerrar && encerrar.mutate(paraEncerrar)}
        loading={encerrar.isPending}
        title="Encerrar recorrência?"
        confirmLabel="Sim, encerrar"
        message={<>A recorrência <strong className="text-white">{paraEncerrar?.description}</strong> recebe a data de fim de hoje e para de gerar lançamentos. Os já criados continuam. Para uma pausa temporária, use o botão de pausar.</>}
      />

      <ConfirmDialog
        open={!!paraExcluir}
        onClose={() => setParaExcluir(null)}
        onConfirm={() => paraExcluir && remover.mutate(paraExcluir.id)}
        loading={remover.isPending}
        title="Desativar recorrência?"
        confirmLabel="Sim, desativar"
        message={<>A recorrência <strong className="text-white">{paraExcluir?.description}</strong> deixa de gerar lançamentos.</>}
      />
    </>
  )
}
